import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Briefcase,
  BookOpen,
  Sparkles,
  Clock,
  Database,
  MapPin,
  Users,
  ExternalLink,
  Beaker,
  Cpu,
} from "lucide-react";
import type { ContactDetailFull } from "@/hooks/useContactDetail";
import { openExternal } from "@/lib/utils";

// ── Props ──────────────────────────────────────────────────────────

interface ContactProfileTabProps {
  contact: ContactDetailFull;
}

// ── Component ──────────────────────────────────────────────────────

export function ContactProfileTab({ contact }: ContactProfileTabProps) {
  const hasClay = !!(
    contact.clay_headline ||
    contact.clay_summary ||
    contact.clay_experience_title ||
    contact.clay_publication_title
  );
  const hasBiography = !!contact.biography;
  const hasSkills = !!(
    contact.therapeutic_focus?.length ||
    contact.technology_focus?.length ||
    contact.enriched_role_description
  );

  if (!hasClay && !hasBiography && !hasSkills) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Database className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm font-medium">No enrichment data available</p>
        <p className="text-xs mt-1">
          Clay enrichment data will appear here once this contact is enriched.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-1">
      {/* ── About Section ──────────────────────────────────────── */}
      {(contact.clay_headline || contact.clay_summary || contact.biography) && (
        <section>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" />
            About
          </h3>
          <div className="space-y-2">
            {contact.clay_headline && (
              <p className="text-sm font-medium">{contact.clay_headline}</p>
            )}
            {contact.clay_summary && (
              <p className="text-xs text-muted-foreground">{contact.clay_summary}</p>
            )}
            {contact.biography && !contact.clay_summary && (
              <p className="text-xs text-muted-foreground">{contact.biography}</p>
            )}
            {contact.biography && contact.clay_summary && (
              <details className="text-xs">
                <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                  Show extended biography
                </summary>
                <p className="mt-1 text-muted-foreground">{contact.biography}</p>
              </details>
            )}

            {/* Location & Network */}
            <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
              {contact.clay_location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {contact.clay_location}
                </span>
              )}
              {contact.clay_connections != null && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {contact.clay_connections.toLocaleString()} connections
                </span>
              )}
              {contact.clay_num_followers != null && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {contact.clay_num_followers.toLocaleString()} followers
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {(contact.clay_headline || contact.clay_summary || contact.biography) && <Separator />}

      {/* ── Experience Section ──────────────────────────────────── */}
      {contact.clay_experience_title && (
        <>
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5" />
              Experience
            </h3>
            <div className="border rounded-md p-3 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{contact.clay_experience_title}</span>
                {contact.clay_experience_is_current && (
                  <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-700 border-green-500/20">
                    Current
                  </Badge>
                )}
              </div>
              {contact.clay_experience_company && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span>{contact.clay_experience_company}</span>
                  {contact.clay_experience_company_domain && (
                    <>
                      <span>·</span>
                      <span>{contact.clay_experience_company_domain}</span>
                    </>
                  )}
                </div>
              )}
              {contact.clay_experience_summary && (
                <p className="text-xs text-muted-foreground mt-1">{contact.clay_experience_summary}</p>
              )}
              {contact.clay_experience_url && (
                <a
                  href={contact.clay_experience_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                >
                  View on LinkedIn <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </section>
          <Separator />
        </>
      )}

      {/* ── Publications Section ───────────────────────────────── */}
      {contact.clay_publication_title && (
        <>
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              Publications & Research
            </h3>
            <div className="border rounded-md p-3 space-y-1">
              <p className="text-sm font-medium">{contact.clay_publication_title}</p>
              {contact.clay_publication_summary && (
                <p className="text-xs text-muted-foreground">{contact.clay_publication_summary}</p>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {contact.clay_publication_publisher && (
                  <span>{contact.clay_publication_publisher}</span>
                )}
                {contact.clay_publication_date && (
                  <>
                    {contact.clay_publication_publisher && <span>·</span>}
                    <span>{contact.clay_publication_date}</span>
                  </>
                )}
              </div>
              {contact.clay_publication_url && (
                <a
                  href={contact.clay_publication_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                >
                  View publication <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </section>
          <Separator />
        </>
      )}

      {/* ── Skills & Expertise Section ─────────────────────────── */}
      {hasSkills && (
        <>
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Skills & Expertise
            </h3>
            <div className="space-y-2">
              {contact.enriched_role_description && (
                <div>
                  <span className="text-xs text-muted-foreground">Inferred Role</span>
                  <p className="text-xs mt-0.5">{contact.enriched_role_description}</p>
                </div>
              )}
              {contact.therapeutic_focus && contact.therapeutic_focus.length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                    <Beaker className="h-3 w-3" /> Therapeutic Focus
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {contact.therapeutic_focus.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {contact.technology_focus && contact.technology_focus.length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                    <Cpu className="h-3 w-3" /> Technology Focus
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {contact.technology_focus.map((t, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
          <Separator />
        </>
      )}

      {/* ── Enrichment Metadata ────────────────────────────────── */}
      {contact.clay_enrichment_status && (
        <section>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            Enrichment Metadata
          </h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="text-muted-foreground">Status</div>
            <div>
              <Badge
                variant="outline"
                className={
                  contact.clay_enrichment_status === "enriched"
                    ? "bg-green-500/10 text-green-700 border-green-500/20 text-[10px]"
                    : "text-[10px]"
                }
              >
                {contact.clay_enrichment_status}
              </Badge>
            </div>
            {contact.clay_enriched_at && (
              <>
                <div className="text-muted-foreground">Enriched At</div>
                <div>
                  {new Date(contact.clay_enriched_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </>
            )}
            {contact.clay_batch_id && (
              <>
                <div className="text-muted-foreground">Batch ID</div>
                <div className="font-mono text-[10px] truncate">{contact.clay_batch_id}</div>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
